import {MenuPanels} from '../../models/UIModel.js';
import {ShoppeBase} from './shoppe-base.js';
import {Column, Container, Panel, Row, Text} from '@wonderlandengine/react-ui/components';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {Carousel, CarouselItem} from '../components/carousel.js';
import React, {useEffect, useRef, useState} from 'react';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {SliderItem} from '../components/SliderComponent.js';

import {StorePowerupsData} from '../../components/store-data/powerups/StorePowerupsData.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {Statistics} from '../../components/statistics.js';
import {Localization} from '../../utils/Localization.js';
import {formatString, insertNewlines, setAlpha} from '../utils/utils.js';
import {Align, FlexDirection, Justify} from '@wonderlandengine/react-ui';
import {colorSwatch} from '../utils/colorSwatch.js';
import {useRotBMaterialContext} from '../utils/rotb-material-context.js';
import {StoreManager} from '../../components/store-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {Header} from '../components/header.js';
import {BuyButton} from '../components/buy-button.js';
import {CoinButton} from '../components/coin-button.js';
import {buyItem} from '../utils/buyItem.js';
import {UnlockManager} from '../../components/unlock-manager.js';
import {Texture} from '@wonderlandengine/api';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {PowerupSelectItem} from '../../components/store-data/powerups/PowerupSelectItem.js';

function isStoreItemLocked(storeItem: StoreInventoryItems) {
    const data = StorePowerupsData.instance.data.get(storeItem);
    return Statistics.instance.getTransactional(data.stat) == 0;
}

function lockImage(storeItem: StoreInventoryItems, image: Texture): Texture {
    if (!isStoreItemLocked(storeItem)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

export const ShoppePowerups = () => {
    const mats = useRotBMaterialContext();

    const [refreshKey, setRefreshKey] = useState(0);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [levelText, setLevelText] = useState('');
    const [inventoryAmount, setInventoryAmount] = useState('');
    const [upgradeDescription, setUpgradeDescription] = useState('');
    const [unlockDescription, setUnlockDescription] = useState('');
    const [inventoryStat, setInventoryStat] = useState<TransactionalNames>(null);
    const [upgradeStat, setUpgradeStat] = useState(null);
    const [upgradeCost, setUpgradeCost] = useState(0);
    const [isLocked, setIsLocked] = useState(true);
    const [isImplemented, setIsImplemented] = useState(true);
    const [upgradeMaxed, setUpgradeMaxed] = useState(false);

    const selectedItem = useRef<SliderItem>(null);

    const onSelectionChanged = (item: SliderItem) => {
        selectedItem.current = item;
        setRefreshKey((prev) => prev + 1);
    };

    useEffect(() => {
        if (!selectedItem.current) {
            return;
        }
        let item = selectedItem.current;
        const storeInventoryItem = StoreInventoryItems[item.id];

        const data = StorePowerupsData.instance.data.get(
            storeInventoryItem
        ) as PowerupSelectItem;
        const descriptions =
            StorePowerupsData.instance.descriptions.get(storeInventoryItem);

        setTitle(Localization.instance.get(data.title));
        setDescription(Localization.instance.get(descriptions.levelUpDescriptions[0]));
        const upgradeLevel = StoreManager.instance.getUpgradeLevel(data.stat);
        let currentDescriptionText = ' ';

        setIsImplemented(data.isImplemented);

        const locked = isStoreItemLocked(storeInventoryItem);
        if (locked) {
            setInventoryAmount(Localization.instance.get('Locked'));
            setLevelText('');
            setUpgradeMaxed(false);
            const upgradeCoinCost = StoreManager.instance.getCoinCost(data.stat);
            setUpgradeCost(upgradeCoinCost);
            setUpgradeDescription('');

            const unlockInstruct = UnlockManager.instance.getInstructionsForUnlock(
                data.lockFlag,
                0
            );
            setUnlockDescription(unlockInstruct.instructions);
        } else {
            if (upgradeLevel > 0) {
                const currentInventory = Statistics.instance.getTransactional(
                    data.inventoryStat
                );
                if (currentInventory == 0) {
                    // do some bouncy thing
                } else {
                    // reset bouncy thing
                }
                setInventoryAmount(
                    formatString(
                        Localization.instance.get('YouGot'),
                        formatNumber(currentInventory)
                    )
                );
                setLevelText(
                    formatString(Localization.instance.get('UpgradeLevel'), upgradeLevel)
                );

                const upgradeCoinCost = StoreManager.instance.getCoinCost(data.stat);
                setUpgradeCost(upgradeCoinCost);
                if (upgradeLevel < descriptions.levelUpDescriptions.length) {
                    setUpgradeMaxed(false);
                    const description = Localization.instance.get(
                        descriptions.levelUpDescriptions[upgradeLevel]
                    );
                    if (upgradeLevel > 0) {
                        const nextUpgrade = Localization.instance.get('NextUpgradeFormat');
                        currentDescriptionText = formatString(nextUpgrade, description);
                    }
                }
                // maxed out
                else {
                    currentDescriptionText = Localization.instance.get(
                        descriptions.levelUpDescriptions[
                            descriptions.levelUpDescriptions.length - 1
                        ]
                    );
                    setUpgradeMaxed(true);
                }
            } else {
                setLevelText('');
            }
        }
        setUpgradeDescription(currentDescriptionText);
        setInventoryStat(data.inventoryStat);
        setUpgradeStat(data.stat);
        setIsLocked(locked);
    }, [refreshKey]);

    useEffect(() => {
        const changeCallback = () => {
            setRefreshKey((prev) => prev + 1);
        };
        Statistics.instance.registerChangedCallback(changeCallback);
        return () => {
            Statistics.instance.unRegisterChangedCallback(changeCallback);
        };
    }, []);

    return (
        <ShoppeBase
            title={Localization.instance.get('Powerups')}
            onOkay={() => goToPreviousMenu(MenuPanels.StorePowerups)}
        >
            <Column>
                <Carousel loop={false} onSelectionChanged={onSelectionChanged}>
                    <CarouselItem
                        title="Coconut Bomb"
                        id={StoreInventoryItems[StoreInventoryItems.coconut]}
                        image={lockImage(
                            StoreInventoryItems.coconut,
                            UiTexturesManager.instance.buttonBuyBomb
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="fruitsmasher"
                        id={StoreInventoryItems[StoreInventoryItems.smasher]}
                        image={lockImage(
                            StoreInventoryItems.smasher,
                            UiTexturesManager.instance.buttonFruitsmasher
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="shield"
                        id={StoreInventoryItems[StoreInventoryItems.barrier]}
                        image={lockImage(
                            StoreInventoryItems.barrier,
                            UiTexturesManager.instance.buttonShield
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="ClockMagnet"
                        id={StoreInventoryItems[StoreInventoryItems.clock]}
                        image={lockImage(
                            StoreInventoryItems.clock,
                            UiTexturesManager.instance.buttonClockMagnet
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="DestroyCountdown"
                        id={StoreInventoryItems[StoreInventoryItems.skull]}
                        image={lockImage(
                            StoreInventoryItems.skull,
                            UiTexturesManager.instance.buttonDestroyCountdown
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="nuke"
                        id={StoreInventoryItems[StoreInventoryItems.nuke]}
                        image={lockImage(
                            StoreInventoryItems.nuke,
                            UiTexturesManager.instance.theNuke
                        )}
                    ></CarouselItem>
                    <CarouselItem
                        title="mushroom_slicer"
                        id={StoreInventoryItems[StoreInventoryItems.mushroom]}
                        image={lockImage(
                            StoreInventoryItems.mushroom,
                            UiTexturesManager.instance.mushroomSlicer
                        )}
                    ></CarouselItem>
                </Carousel>
                <Header
                    title={title}
                    color={colorSwatch.SubTitleHighBackground}
                    center={true}
                />
                <Container height={75} alignContent={Align.FlexStart} padding={10}>
                    <Text
                        color={setAlpha(colorSwatch.DescriptionText, '90%')}
                        material={mats.regularTextMaterial}
                        fontSize={20}
                        textAlign="center"
                    >
                        {insertNewlines(description, 48)}
                    </Text>
                </Container>
                {isLocked ? (
                    <Header title={Localization.instance.get('UnlockNow')} />
                ) : (
                    <Header
                        title={upgradeMaxed ? 'UPGRADE MAX' : 'UPGRADE'}
                        subtitle={levelText}
                    />
                )}
                {isImplemented ? (
                    <Container
                        height={70}
                        marginTop={10}
                        justifyContent={Justify.SpaceBetween}
                        alignContent={Align.Center}
                        flexDirection={FlexDirection.Row}
                    >
                        <Container flexGrow={1}>
                            <Text
                                color={setAlpha(colorSwatch.DescriptionText, '90%')}
                                material={mats.regularTextMaterial}
                                fontSize={18}
                            >
                                {insertNewlines(upgradeDescription, 36)}
                            </Text>
                        </Container>

                        <Container
                            top={-5}
                            flexGrow={0}
                            alignSelf={Align.FlexEnd}
                            justifyContent={Justify.FlexEnd}
                            marginBottom={4}
                        >
                            {!upgradeMaxed && (
                                <CoinButton
                                    price={upgradeCost}
                                    onClick={() => {
                                        buyItem(upgradeStat, upgradeCost, 1);
                                    }}
                                />
                            )}
                        </Container>
                    </Container>
                ) : (
                    <Panel
                        rounding={4}
                        height={75}
                        justifyContent={Justify.Center}
                        alignContent={Align.FlexStart}
                        padding={10}
                        backgroundColor={colorSwatch.Window}
                        margin={10}
                    >
                        <Text fontSize={20} textAlign="center">
                            {Localization.instance.get('ComingSoon')}
                        </Text>
                    </Panel>
                )}
                {!isLocked ? (
                    <Header
                        title={Localization.instance.get('InventoryTitle')}
                        subtitle={inventoryAmount}
                    />
                ) : (
                    <Header title={Localization.instance.get('UnlockRequirements')} />
                    //TK: Should be centered, but that doesn't work at the moment
                    // https://github.com/WonderlandEngine/wonderland-react-ui/issues/20
                )}
                {!isLocked && inventoryStat && isImplemented && (
                    <Row flexGrow={1} padding={10} justifyContent={Justify.SpaceBetween}>
                        <BuyButton stat={inventoryStat} costIndex={0} />
                        <BuyButton stat={inventoryStat} costIndex={1} />
                        <BuyButton stat={inventoryStat} costIndex={2} />
                    </Row>
                )}
                )
                {isLocked && isImplemented && (
                    <Row flexGrow={1} padding={10} justifyContent={Justify.SpaceBetween}>
                        <Text
                            color={setAlpha(colorSwatch.DescriptionText, '90%')}
                            material={mats.regularTextMaterial}
                            fontSize={18}
                        >
                            {insertNewlines(unlockDescription, 36)}
                        </Text>
                    </Row>
                )}
            </Column>
        </ShoppeBase>
    );
};
